import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { HOMEOWNER_SUPPORT_URL, TECHNICAL_SUPPORT_DISPLAY, TECHNICAL_SUPPORT_TEL } from '@ld.consumer-portal/constants';
import { heroiconsWarning } from '@ld.consumer-portal/data-uri';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export interface DeletePaymentReason {
  code: string;
  description: string;
}

export interface DeletePaymentRequest {
  paymentId: string;
  loanNumber: string;
  reasonCode: string;
  reasonDescription: string;
  otherReason?: string;
}

const DELETE_REASONS: DeletePaymentReason[] = [
  { code: 'ALREADY_PAID', description: 'My payment has already been made' },
  { code: 'PAYING_ANOTHER_WAY', description: "I'll be paying another way" },
  { code: 'PAYING_OFF_LOAN', description: "I'm paying off my loan before the payment is due" },
  { code: 'FINANCIAL_HARDSHIP', description: 'I have a financial hardship and may not be able to pay' },
  { code: 'OTHER', description: 'Other - Please Specify' },
];

@Component({
  selector: 'ld-delete-payment-modal',
  imports: [
    BaseModalComponent,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    RouterLink,
    TranslocoDirective,
  ],
  templateUrl: './delete-payment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeletePaymentModalComponent {
  paymentId = input.required<string>();
  loanNumber = input.required<string>();
  paymentAmount = input(0);
  paymentDate = input<Date | null>(null);
  isOpen = input(false);

  delete = output<DeletePaymentRequest>();
  cancel = output<void>();

  protected readonly heroiconsWarning = heroiconsWarning;
  protected readonly reasons = DELETE_REASONS;
  protected readonly selectedReason = signal<DeletePaymentReason | null>(null);
  protected readonly otherReasonText = signal('');
  protected readonly isDeleting = signal(false);

  protected readonly supportResourcesUrl = HOMEOWNER_SUPPORT_URL;
  protected readonly technicalSupportPhone = TECHNICAL_SUPPORT_DISPLAY;
  protected readonly technicalSupportTel = TECHNICAL_SUPPORT_TEL;

  protected readonly isFinancialHardship = () => this.selectedReason()?.code === 'FINANCIAL_HARDSHIP';
  protected readonly isOtherReason = () => this.selectedReason()?.code === 'OTHER';

  protected readonly canDelete = () => {
    const reason = this.selectedReason();
    if (!reason) return false;
    if (reason.code === 'OTHER' && !this.otherReasonText().trim()) return false;
    return true;
  };

  protected onReasonChange(reason: DeletePaymentReason): void {
    this.selectedReason.set(reason);
    if (reason.code !== 'OTHER') {
      this.otherReasonText.set('');
    }
  }

  protected onOtherReasonChange(text: string): void {
    this.otherReasonText.set(text);
  }

  protected onDelete(): void {
    const reason = this.selectedReason();
    if (!reason || !this.canDelete()) return;

    this.isDeleting.set(true);

    const request: DeletePaymentRequest = {
      paymentId: this.paymentId(),
      loanNumber: this.loanNumber(),
      reasonCode: reason.code,
      reasonDescription: reason.description,
    };

    if (reason.code === 'OTHER') {
      request.otherReason = this.otherReasonText().trim();
    }

    this.delete.emit(request);
  }

  protected onCancel(): void {
    this.resetForm();
    this.cancel.emit();
  }

  private resetForm(): void {
    this.selectedReason.set(null);
    this.otherReasonText.set('');
    this.isDeleting.set(false);
  }
}
